'use client';

// Enhanced SSR prevention configuration to avoid Firebase conflicts
export const dynamic = 'force-dynamic';
export const fetchCache = 'force-no-store';

import { useState, useEffect, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, type User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { app, db } from '../../lib/firebase';
import { handleTimestamp } from '@/lib/firebase-data';
import { getAuthStabilizationDelay } from '@/lib/auth-timing';
import { toastInfo } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, Eye, EyeOff, Terminal, RefreshCw, LogIn } from 'lucide-react';
import Link from 'next/link';
import { useRecaptchaVerification } from '../../hooks/useRecaptchaVerification';
import type { InterpreterPublicProfile, InterpreterPrivateProfile } from '../../lib/types';

// ✅ Enhanced rate limiting to prevent brute force attacks
const RATE_LIMIT_WINDOW = 300000; // 5 minutes
const MAX_LOGIN_ATTEMPTS = 5;

// ✅ Updated helper function using unified timestamp handling
const convertTimestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  
  try {
    return handleTimestamp(timestamp);
  } catch (error) {
    console.warn('Error converting timestamp:', error);
    return null;
  }
};

// ✅ Enhanced authentication state interface
interface AuthState {
  user: User | null;
  loading: boolean;
  checking: boolean;
  redirecting: boolean;
}

// ✅ Separate component that uses useSearchParams()
function LoginFormContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<number | null>(null);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    checking: true,
    redirecting: false
  });
  
  const router = useRouter();
  const searchParams = useSearchParams();
  const { executeRecaptcha, isRecaptchaReady } = useRecaptchaVerification();

  const redirect = searchParams.get('redirect') || '/profile';
  const message = searchParams.get('message');
  const reason = searchParams.get('reason');

  // ✅ Ensures the interpreter/client role check runs at most once per
  // signed-in session, and lets us cancel a pending redirect on unmount.
  const roleCheckedRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ✅ Resolves which account type (if any) this authenticated user has.
  // Checks interpreters/{uid} first (this is the interpreter login page), then
  // falls back to clients/{uid} before concluding the signup is incomplete.
  // Firestore failures resolve to 'error' rather than 'not-found' so callers
  // never mistake a transient read failure for a missing profile.
  const resolveAccountRole = async (currentUser: User): Promise<'interpreter' | 'client' | 'not-found' | 'error'> => {
    console.log('🔄 Resolving account role for user:', currentUser.uid);

    if (!db) {
      console.error('❌ Firestore database not available');
      return 'error';
    }

    try {
      const publicSnap = await getDoc(doc(db, 'interpreters', currentUser.uid));
      if (publicSnap.exists()) {
        console.log('✅ Interpreter profile verified');
        return 'interpreter';
      }
    } catch (error: any) {
      console.error('❌ Interpreter profile check failed:', error);
      return 'error';
    }

    try {
      const clientSnap = await getDoc(doc(db, 'clients', currentUser.uid));
      if (clientSnap.exists()) {
        console.log('✅ Client account verified');
        return 'client';
      }
    } catch (error: any) {
      console.error('❌ Client profile check failed:', error);
      return 'error';
    }

    console.warn('⚠️ No interpreter or client profile found');
    return 'not-found';
  };

  // ✅ Enhanced URL parameter handling
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam && !email) {
      setEmail(decodeURIComponent(emailParam));
    }

    // ✅ Enhanced reason handling
    if (reason) {
      switch (reason) {
        case 'not-authenticated':
          setError('Please log in to access your account.');
          break;
        case 'session-expired':
          setError('Your session has expired. Please log in again.');
          break;
        case 'account-created':
          setSuccessMessage('Account created successfully! Please log in with your credentials.');
          break;
        case 'password-reset':
          setSuccessMessage('Password updated successfully! Please log in with your new password.');
          break;
        case 'profile-not-found':
        case 'profile-incomplete':
          setError('Profile setup incomplete. Please log in and complete your interpreter registration.');
          break;
        case 'verification-required':
          setSuccessMessage('Welcome! Consider verifying your email to boost your profile credibility.');
          break;
        default:
          if (reason) {
            setError('Authentication required. Please log in to continue.');
          }
      }
    }

    if (message === 'signup-success' || message === 'registration-success') {
      setSuccessMessage('Welcome to Talksie! Your account was created successfully. Please log in to access your interpreter dashboard.');
    }
  }, [searchParams, email, reason, message]);

  // ✅ Enhanced Firebase configuration check
  useEffect(() => {
    if (!app) {
      setConfigError('Firebase is not properly configured. Please contact our support team for assistance.');
      console.error('❌ Firebase app not initialized in login page');
    }
  }, []);

  // ✅ FIXED: Simplified authentication state management
  useEffect(() => {
    if (!app || configError) {
      setAuthState(prev => ({ ...prev, loading: false, checking: false }));
      return;
    }

    const auth = getAuth(app);
    let cancelled = false;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    // ✅ Auth state listener — role check runs once per signed-in session,
    // only after auth has settled to a non-null user.
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔐 Auth state changed:', currentUser ? `User: ${currentUser.uid}` : 'No user');

      if (currentUser) {
        if (settleTimer) {
          clearTimeout(settleTimer);
          settleTimer = undefined;
        }

        if (roleCheckedRef.current) return;
        roleCheckedRef.current = true;

        setAuthState(prev => ({ ...prev, user: currentUser, checking: true, redirecting: true }));

        // ✅ Log verification status but don't block access
        if (!currentUser.emailVerified) {
          console.log('📧 User email not verified (but allowing access):', currentUser.email);
        } else {
          console.log('✅ User email verified:', currentUser.email);
        }

        const role = await resolveAccountRole(currentUser);

        if (role === 'interpreter') {
          console.log('✅ Profile verified, redirecting to:', redirect);
          setSuccessMessage('Welcome back!');
          redirectTimerRef.current = setTimeout(() => {
            router.push(redirect);
          }, 1000);
        } else if (role === 'client') {
          console.log('✅ Client account detected, redirecting to client dashboard');
          toastInfo('This is a client account — taking you to your dashboard.', 'Client Account');
          setSuccessMessage('Welcome back! Redirecting to your dashboard...');
          redirectTimerRef.current = setTimeout(() => {
            router.push('/client-dashboard');
          }, 1000);
        } else if (role === 'not-found') {
          console.log('❌ No interpreter or client profile found, redirecting to signup');
          setError('Profile not found. Please complete your interpreter registration.');
          redirectTimerRef.current = setTimeout(() => {
            router.push('/signup?step=complete&reason=profile-not-found');
          }, 2000);
        } else {
          // ✅ Firestore read failed — never log out or redirect on a transient
          // error, just let the user retry.
          console.error('❌ Account role check failed');
          roleCheckedRef.current = false;
          setError('Could not verify your account right now. Please refresh and try again.');
          setAuthState(prev => ({ ...prev, loading: false, checking: false, redirecting: false }));
        }
      } else {
        // ✅ Not terminal yet — the first onAuthStateChanged firing can be a
        // transient null while Firebase is still restoring a persisted
        // session in this environment (see useAuth()'s "Firebase Studio
        // timing fix"). Give it the same settle window, then re-check
        // auth.currentUser directly before concluding the user is really
        // logged out. Still resolving keeps the checking screen up; it
        // never shows the form prematurely.
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          if (cancelled) return;
          if (auth.currentUser) return; // a real user arrived — handled above

          console.log('👤 No authenticated user, staying on login page');
          roleCheckedRef.current = false;
          setAuthState({
            user: null,
            loading: false,
            checking: false,
            redirecting: false
          });
        }, getAuthStabilizationDelay());
      }
    });

    return () => {
      cancelled = true;
      if (settleTimer) clearTimeout(settleTimer);
      unsubscribe();
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [app, configError, router, redirect]);

  // ✅ Enhanced cooldown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (cooldownTime > 0) {
      interval = setInterval(() => {
        setCooldownTime(prev => {
          if (prev <= 1) {
            setAttempts(0);
            setLastAttemptTime(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownTime]);

  // ✅ Enhanced input validation
  const validateInputs = (): boolean => {
    setError(null);

    if (!email.trim()) {
      setError('Email address is required.');
      return false;
    }

    if (!password.trim()) {
      setError('Password is required.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address.');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }

    return true;
  };

  // ✅ Enhanced rate limiting check
  const checkRateLimit = (): boolean => {
    const now = Date.now();

    if (lastAttemptTime && (now - lastAttemptTime < RATE_LIMIT_WINDOW)) {
      if (attempts >= MAX_LOGIN_ATTEMPTS) {
        const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - (now - lastAttemptTime)) / 1000);
        setCooldownTime(remainingTime);
        setError(`Too many login attempts. Please wait ${Math.ceil(remainingTime / 60)} minutes before trying again, or reset your password.`);
        return false;
      }
    } else if (lastAttemptTime && (now - lastAttemptTime >= RATE_LIMIT_WINDOW)) {
      setAttempts(0);
      setLastAttemptTime(null);
    }

    return true;
  };

  // ✅ Enhanced login submission handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInputs() || !checkRateLimit()) {
      return;
    }

    if (!app) {
      setError('Authentication service is not available. Please try again later or contact support.');
      return;
    }

    setLoading(true);
    setSuccessMessage(null);
    setError(null);
    const trimmedEmail = email.trim();

    try {
      // ✅ Enhanced reCAPTCHA verification
      if (attempts >= 2 && isRecaptchaReady) {
        console.log('🔐 Executing reCAPTCHA for enhanced security...');
        const recaptchaToken = await executeRecaptcha('login');

        if (!recaptchaToken) {
          setError('Security verification failed. Please try again or contact support if the issue persists.');
          setLoading(false);
          return;
        }
        console.log('✅ reCAPTCHA verification successful');
      }

      // ✅ Update rate limiting tracking
      const now = Date.now();
      setAttempts(prev => prev + 1);
      setLastAttemptTime(now);

      const auth = getAuth(app);
      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);

      console.log('✅ Authentication successful for user:', userCredential.user.uid);

      // Reset attempts on successful login
      setAttempts(0);
      setLastAttemptTime(null);

      // ✅ Show immediate success message
      setSuccessMessage('Login successful! Redirecting to your profile...');

      // The onAuthStateChanged listener will handle the redirection
      
    } catch (err: any) {
      console.error('❌ Login error:', err);

      let errorMessage = 'Login failed. Please try again.';
      let shouldSuggestPasswordReset = false;

      // ✅ Enhanced error handling with specific guidance
      switch (err.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address. Please check your email or create a new account.';
          break;
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
          errorMessage = 'Incorrect email or password. Please check your credentials and try again.';
          shouldSuggestPasswordReset = true;
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Account temporarily locked due to too many failed attempts. Please reset your password or try again later.';
          setCooldownTime(600); // 10 minute cooldown
          shouldSuggestPasswordReset = true;
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network connection error. Please check your internet connection and try again.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact our support team for assistance.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format. Please enter a valid email address.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password authentication is not enabled. Please contact support.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak. Please choose a stronger password.';
          break;
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists. Please try logging in instead.';
          break;
        case 'auth/requires-recent-login':
          errorMessage = 'Please log out and log back in to continue with this action.';
          break;
        default:
          console.error('Unexpected login error:', {
            code: err.code,
            message: err.message,
            email: trimmedEmail,
            timestamp: new Date().toISOString()
          });

          if (err.message?.includes('network')) {
            errorMessage = 'Network error occurred. Please check your connection and try again.';
          } else if (err.message?.includes('timeout')) {
            errorMessage = 'Login request timed out. Please try again.';
          } else {
            errorMessage = `Login failed: ${err.message || 'An unexpected error occurred'}`;
          }
      }

      // ✅ Enhanced error message with helpful suggestions
      if (shouldSuggestPasswordReset && attempts >= 2) {
        errorMessage += ' If you continue having trouble, try resetting your password.';
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  // ✅ Show loading/redirecting state
  if (authState.checking || authState.redirecting) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <div className="flex justify-center items-center min-h-screen py-12 px-4">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="text-gray-400">
              {authState.redirecting ? 'Redirecting to your profile...' : 'Checking authentication...'}
            </p>
            {successMessage && (
              <p className="text-green-400 text-sm">{successMessage}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ✅ Enhanced Firebase configuration error screen
  if (configError) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <div className="flex justify-center items-center min-h-screen py-12 px-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <Link href="/" className="inline-block">
                <h1 className="text-4xl font-black tracking-tight text-white hover:text-gray-300 transition-colors">
                  Talksie
                </h1>
              </Link>
            </div>

            <Alert variant="destructive" className="bg-red-900/20 border-red-500/50">
              <Terminal className="h-4 w-4 text-red-400" />
              <AlertTitle className="text-red-200">Service Unavailable</AlertTitle>
              <AlertDescription className="text-red-300">{configError}</AlertDescription>
            </Alert>

            <div className="text-center space-y-4">
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-white text-black hover:bg-gray-100"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button
                variant="outline"
                asChild
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                <Link href="/">Return to Home</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Post-it Sticker for theFiF */}
      <div className="absolute top-4 right-4 z-20 hidden md:block">
        <div className="relative group cursor-pointer">

          {/* Drop shadow layers — offset bottom-right to imply paper lifting off wall */}
          <div className="absolute top-3 left-2 w-52 h-40 bg-black/25 rounded-sm transform rotate-3 blur-md"></div>
          <div className="absolute top-1.5 left-1 w-52 h-40 bg-black/10 rounded-sm transform rotate-1 blur-sm"></div>

          {/* Main post-it note — varied multi-stop yellow, not uniform */}
          <div
            className="relative w-52 h-40 text-gray-900 p-4 rounded-sm transform rotate-2 group-hover:rotate-1 transition-all duration-500 ease-out overflow-hidden"
            style={{
              background: 'linear-gradient(150deg, #fef9c3 0%, #fde047 18%, #facc15 40%, #fde68a 58%, #fbbf24 76%, #fef08a 90%, #fde047 100%)',
              boxShadow: '4px 6px 12px rgba(0,0,0,0.28), 2px 3px 5px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.55)',
            }}
          >

            {/* Tape strip at top — semi-transparent, slightly glossy */}
            <div
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-[14px] rounded-[2px]"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.30) 48%, rgba(255,255,255,0.55) 100%)',
                border: '1px solid rgba(255,255,255,0.45)',
                boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.9), 0 1px 3px rgba(0,0,0,0.12)',
              }}
            ></div>

            {/* Pencil/pen line texture — evenly spaced, very subtle */}
            <div className="absolute inset-0 opacity-[0.11] pointer-events-none">
              {[32, 44, 56, 68, 80, 92, 104, 116, 128].map(top => (
                <div key={top} className="absolute left-3 right-3 h-px bg-amber-800" style={{ top }}></div>
              ))}
            </div>

            {/* Bottom-right paper curl — pronounced lift with shadow underneath */}
            {/* Shadow under the curl */}
            <div
              className="absolute bottom-0 right-0 pointer-events-none"
              style={{
                width: '32px',
                height: '32px',
                background: 'radial-gradient(ellipse at 100% 100%, rgba(0,0,0,0.22) 0%, transparent 70%)',
              }}
            ></div>
            {/* The curled-up flap (reverse side of paper, slightly lighter) */}
            <div
              className="absolute bottom-0 right-0 pointer-events-none"
              style={{
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 0 28px 28px',
                borderColor: 'transparent transparent #fef9c3 transparent',
                filter: 'drop-shadow(-1px -1px 2px rgba(0,0,0,0.18))',
              }}
            ></div>
            {/* Gradient shading across the curl fold */}
            <div
              className="absolute bottom-0 right-0 pointer-events-none"
              style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, transparent 48%, rgba(0,0,0,0.14) 100%)',
                clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
              }}
            ></div>

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col">
              <div className="mb-3">
                <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight tracking-wide">
                  theFiF
                </h3>
                <p className="text-xs text-gray-800 leading-relaxed font-medium">
                  A library of free resources for Talksie's interpreters and translators, available to all freelancers
                </p>
              </div>

              {/* Enter button */}
              <div className="mt-auto">
                <a
                  href="https://thefif.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-gray-900 text-yellow-200 px-3 py-1.5 rounded text-xs font-semibold hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  enter
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center items-center min-h-screen py-12 px-4">
        <div className="w-full max-w-md space-y-6">
          {/* ✅ Enhanced Talksie Branding */}
          <div className="text-center">
            <Link href="/" className="inline-block">
              <h1 className="text-4xl font-black tracking-tight text-white hover:text-gray-300 transition-colors">
                Talksie
              </h1>
            </Link>
            <p className="text-gray-400 mt-2">Professional Interpreter & Translator Platform</p>
          </div>

          <Card className="w-full shadow-lg border-gray-800 bg-gray-900">
            <form onSubmit={handleLogin}>
              <CardHeader className="text-center space-y-2">
                <div className="flex items-center justify-center mb-4">
                  <LogIn className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-white">Welcome Back</CardTitle>
                <CardDescription className="text-gray-400">
                  Sign in to your account
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* ✅ Enhanced success message */}
                {successMessage && (
                  <Alert className="bg-green-900/20 border-green-500/50">
                    <AlertCircle className="h-4 w-4 text-green-400" />
                    <AlertDescription className="text-green-300">{successMessage}</AlertDescription>
                  </Alert>
                )}

                {/* ✅ Enhanced error handling */}
                {error && (
                  <Alert variant="destructive" className="bg-red-900/20 border-red-500/50">
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <AlertDescription className="text-red-300">{error}</AlertDescription>
                  </Alert>
                )}

                {/* ✅ Enhanced rate limiting indicator */}
                {cooldownTime > 0 && (
                  <Alert className="bg-yellow-900/20 border-yellow-500/50">
                    <AlertCircle className="h-4 w-4 text-yellow-400" />
                    <AlertTitle className="text-yellow-200">Account Temporarily Locked</AlertTitle>
                    <AlertDescription className="text-yellow-300">
                      Please wait {Math.ceil(cooldownTime / 60)} minutes before trying again, or{' '}
                      <Link
                        href={`/forgot-password?email=${encodeURIComponent(email)}`}
                        className="underline hover:text-yellow-100"
                      >
                        reset your password
                      </Link>.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    disabled={loading || cooldownTime > 0}
                    className="h-11 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      disabled={loading || cooldownTime > 0}
                      className="h-11 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      disabled={loading || cooldownTime > 0}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* ✅ Enhanced attempt counter */}
                {attempts > 0 && attempts < MAX_LOGIN_ATTEMPTS && cooldownTime === 0 && (
                  <div className="text-xs text-gray-500 text-center">
                    {MAX_LOGIN_ATTEMPTS - attempts} attempts remaining before temporary lockout
                  </div>
                )}
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  className="w-full h-11 bg-white text-black hover:bg-gray-100"
                  disabled={loading || cooldownTime > 0 || authState.redirecting}
                >
                  {loading || authState.redirecting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                      {authState.redirecting ? 'Redirecting...' : 'Signing in...'}
                    </>
                  ) : cooldownTime > 0 ? (
                    `Locked for ${Math.ceil(cooldownTime / 60)}m`
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <div className="text-sm text-center space-y-2">
                  <Link
                    href={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                    className="text-gray-400 hover:text-white transition-colors block"
                  >
                    Forgot your password?
                  </Link>

                  <p className="text-gray-400">
                    Don't have an account?{' '}
                    <Link
                      href="/signup"
                      className="text-white hover:text-gray-300 font-medium underline"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </CardFooter>
            </form>
          </Card>

          {/* ✅ Enhanced Footer */}
          <div className="text-center space-y-2">
            <p className="text-xs text-gray-500">
              Need help? Contact our{' '}
              <Link href="/contact" className="underline hover:text-white transition-colors">
                support team
              </Link>
            </p>
            <p className="text-xs text-gray-600">
              Professional B2B interpretation services • Secure authentication
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ✅ Enhanced main page component with proper client-side pattern
export default function LoginPage() {
  const [mounted, setMounted] = useState(false);

  // ✅ Enhanced client-side mounting to prevent hydration issues
  useEffect(() => {
    setMounted(true);

    // ✅ Enhanced client-side metadata updates
    document.title = 'Login - Talksie | Professional B2B Interpreter & Translator Platform';

    const updateMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    updateMeta('description', 'Login to your Talksie interpreter account. Access your profile, manage bookings, and connect with clients seeking professional B2B interpretation services.');
    updateMeta('keywords', 'interpreter login, Talksie login, B2B interpretation, professional interpreters, language services');
    
    // Add viewport meta tag for mobile optimization
    updateMeta('viewport', 'width=device-width, initial-scale=1.0');
  }, []);

  // ✅ Enhanced loading state during SSR hydration
  if (!mounted) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="flex justify-center items-center min-h-screen py-12 px-4">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="text-gray-400">Loading authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen bg-black text-white">
        <div className="flex justify-center items-center min-h-screen py-12 px-4">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            <p className="text-gray-400">Loading login form...</p>
          </div>
        </div>
      </div>
    }>
      <LoginFormContent />
    </Suspense>
  );
}
